import React, { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { Bank, Plus, Trash, PencilSimple, Clock, LockSimple, ArrowsClockwise, CheckCircle, XCircle, CurrencyInr } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import AddLoanDialog from '@/components/AddLoanDialog';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCY_SYMBOLS = { INR: '\u20B9', USD: '$', EUR: '\u20AC', GBP: '\u00A3' };

const STATUS_META = {
  in_verification: { label: 'In Verification', cls: 'bg-yellow-400/10 border-yellow-400/30 text-yellow-400', Icon: Clock },
  verified_locked: { label: 'Verified & Locked', cls: 'bg-green-500/10 border-green-500/30 text-green-500', Icon: LockSimple },
  fee_paid: { label: 'In Inter-Cycle Engine', cls: 'bg-blue-500/10 border-blue-500/30 text-blue-400', Icon: ArrowsClockwise },
  settling: { label: 'Settlement In Process', cls: 'bg-yellow-400/10 border-yellow-400/30 text-yellow-400', Icon: ArrowsClockwise },
  closed: { label: 'Loan Closed', cls: 'bg-green-500/10 border-green-500/30 text-green-500', Icon: CheckCircle },
  rejected: { label: 'Rejected', cls: 'bg-red-500/10 border-red-500/30 text-red-400', Icon: XCircle },
};

function LoanItem({ loan, isDark, symbol, onDelete, onEdit }) {
  const meta = STATUS_META[loan.status] || STATUS_META.in_verification;
  const Icon = meta.Icon;
  const spinning = loan.status === 'settling' || loan.status === 'fee_paid';
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
      className={`p-3.5 border-b last:border-b-0 ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`} data-testid={`loan-item-${loan.loan_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`font-ibm text-sm font-medium truncate ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{loan.institution}</span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded-sm ${meta.cls}`} data-testid={`loan-status-${loan.loan_id}`}>
              <Icon size={10} weight="bold" className={spinning ? 'animate-spin' : ''} style={spinning ? { animationDuration: '2s' } : {}} />
              <span className="font-ibm text-[10px] font-semibold uppercase tracking-wider">{meta.label}</span>
            </span>
          </div>
          <div className={`font-ibm text-xs space-y-0.5 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
            <p>A/C: {loan.account_number}</p>
            {loan.branch_city && <p>{loan.branch_city}</p>}
            <p className={isDark ? 'text-zinc-500' : 'text-zinc-500'}>Platform fee (20%): {symbol}{(loan.fee || 0).toLocaleString()}</p>
            {loan.status === 'settling' && <p className="text-yellow-400 font-semibold">Settlement in process — will be paid off from the pool.</p>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={`font-outfit text-lg font-bold ${loan.status === 'closed' ? 'line-through' : ''} ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>
            {symbol}{(loan.loan_amount || 0).toLocaleString()}
          </span>
          {loan.status === 'in_verification' && (
            <>
              <button onClick={() => onEdit(loan)} className={`p-1.5 rounded-sm transition-colors ${isDark ? 'hover:bg-zinc-800 text-zinc-500 hover:text-yellow-400' : 'hover:bg-zinc-100 text-zinc-400 hover:text-yellow-500'}`} data-testid={`edit-loan-${loan.loan_id}`}>
                <PencilSimple size={14} weight="bold" />
              </button>
              <button onClick={() => onDelete(loan)} className={`p-1.5 rounded-sm transition-colors ${isDark ? 'hover:bg-zinc-800 text-zinc-500 hover:text-red-400' : 'hover:bg-zinc-100 text-zinc-400 hover:text-red-500'}`} data-testid={`delete-loan-${loan.loan_id}`}>
                <Trash size={14} weight="bold" />
              </button>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function LoansArea({ loans = [], currency, onRefresh }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  const [addOpen, setAddOpen] = useState(false);
  const [editLoan, setEditLoan] = useState(null);

  const handleDelete = async (loan) => {
    try {
      await axios.delete(`${API}/loans/${loan.loan_id}`, { withCredentials: true });
      toast.success('Loan removed');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const active = loans.filter(l => l.status !== 'closed' && l.status !== 'rejected');

  return (
    <div className="mt-6" data-testid="loans-area">
      <div className={`border rounded-md overflow-hidden ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`} data-testid="loans-section">
        <div className={`p-4 flex items-center justify-between ${isDark ? 'bg-zinc-900' : 'bg-zinc-50'}`}>
          <div className="flex items-center gap-2">
            <Bank size={18} weight="bold" className="text-yellow-400" />
            <div>
              <h3 className={`font-outfit text-base font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Loans (Banks or NBFCs)</h3>
              <p className={`font-ibm text-[10px] uppercase tracking-wider mt-0.5 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Verified by our team · cleared via inter-cycles</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {active.length > 0 && (
              <span className={`font-ibm text-[10px] font-semibold px-1.5 py-0.5 rounded-sm ${isDark ? 'bg-zinc-800 text-zinc-400' : 'bg-zinc-200 text-zinc-600'}`}>{active.length}</span>
            )}
            <button onClick={() => setAddOpen(true)} className="w-8 h-8 bg-yellow-400 text-zinc-950 rounded-sm flex items-center justify-center hover:bg-yellow-500 active:scale-[0.95] transition-all" data-testid="add-loan-button">
              <Plus size={16} weight="bold" />
            </button>
          </div>
        </div>

        <div className={`${isDark ? 'bg-zinc-950' : 'bg-white'} ${loans.length > 5 ? 'max-h-[360px] overflow-y-auto' : ''}`} data-testid="loans-list">
          <AnimatePresence>
            {loans.length === 0 ? (
              <div className={`p-8 text-center font-ibm text-sm ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
                <CurrencyInr size={26} weight="thin" className="mx-auto mb-2 opacity-40" />
                No loans added. Add a bank/NBFC loan to clear it through inter-cycle netting.
              </div>
            ) : (
              loans.map(loan => (
                <LoanItem key={loan.loan_id} loan={loan} isDark={isDark} symbol={symbol} onDelete={handleDelete} onEdit={(l) => setEditLoan(l)} />
              ))
            )}
          </AnimatePresence>
        </div>
      </div>

      <AddLoanDialog open={addOpen} onClose={() => setAddOpen(false)} currency={currency} onSuccess={onRefresh} />
      <AddLoanDialog open={!!editLoan} loan={editLoan} onClose={() => setEditLoan(null)} currency={currency} onSuccess={onRefresh} />
    </div>
  );
}
